import { useMemo } from 'react';

import { Badge, KeyValueRow, KeyValueTable, Text } from '@/design-system/components/ui';

import { Trigger } from '../../domain';
import { OrderlyRevenueType, OrderlyVolumeType } from '../../types';

interface Props {
  trigger: Trigger;
}

export function OrderlyTriggerFullDetails({ trigger }: Props) {
  const volumeTypeDisplay = useMemo(() => {
    const volumeType = trigger.context?.volumeType as OrderlyVolumeType;

    const volumeTypeLabels: Record<OrderlyVolumeType, string> = {
      [OrderlyVolumeType.AllVolume]: 'All Volume',
      [OrderlyVolumeType.TakerVolume]: 'Taker Volume',
      [OrderlyVolumeType.MakerVolume]: 'Maker Volume',
    };

    return volumeTypeLabels[volumeType] || volumeType || '-';
  }, [trigger.context?.volumeType]);

  const revenueTypeDisplay = useMemo(() => {
    const revenueType = trigger.context?.revenueType as OrderlyRevenueType;

    const revenueTypeLabels: Record<OrderlyRevenueType, string> = {
      [OrderlyRevenueType.TotalFee]: 'Total Fee',
      [OrderlyRevenueType.BrokerFee]: 'Broker Fee',
      [OrderlyRevenueType.RealizedPnl]: 'Realized PnL',
    };

    return revenueTypeLabels[revenueType] || revenueType || '-';
  }, [trigger.context?.revenueType]);

  const brokerDisplay = useMemo(() => {
    const brokerId = trigger.context?.brokerId;

    if (!brokerId) return '-';

    return <Badge size="sm">{brokerId}</Badge>;
  }, [trigger.context?.brokerId]);

  const executedWhenDisplay = useMemo(() => {
    return (
      <div className="tw:flex tw:flex-row tw:gap-2 tw:items-center">
        <Text as="span" variant="body-sm" color="muted">
          User trades on
        </Text>
        <Badge size="sm">Orderly</Badge>
        <Text as="span" variant="body-sm" color="muted">
          platform with {volumeTypeDisplay.toLowerCase()}
        </Text>
      </div>
    );
  }, [volumeTypeDisplay]);

  const contextDisplay = useMemo(() => {
    const context = trigger.context;
    if (!context) return '-';

    return (
      <div className="tw:flex tw:flex-col tw:gap-2">
        <div className="tw:flex tw:flex-row tw:gap-2 tw:items-center">
          <Text as="span" variant="body-sm" color="muted">
            Broker ID:
          </Text>
          <Text as="span" variant="body-sm">
            {context.brokerId || '-'}
          </Text>
        </div>
        <div className="tw:flex tw:flex-row tw:gap-2 tw:items-center">
          <Text as="span" variant="body-sm" color="muted">
            Volume Type:
          </Text>
          <Text as="span" variant="body-sm">
            {context.volumeType || '-'}
          </Text>
        </div>
        <div className="tw:flex tw:flex-row tw:gap-2 tw:items-center">
          <Text as="span" variant="body-sm" color="muted">
            Revenue Type:
          </Text>
          <Text as="span" variant="body-sm">
            {context.revenueType || '-'}
          </Text>
        </div>
      </div>
    );
  }, [trigger.context]);

  return (
    <KeyValueTable>
      <KeyValueRow label="Name" value={trigger.name} />
      <KeyValueRow label="Description" value={trigger.description} />
      <KeyValueRow label="Executed when" value={executedWhenDisplay} />
      <KeyValueRow label="Volume Type" value={volumeTypeDisplay} />
      <KeyValueRow label="Revenue Type" value={revenueTypeDisplay} />
      <KeyValueRow label="Broker" value={brokerDisplay} />
      <KeyValueRow label="Volume Expression" value={trigger.volumeExpression ?? '-'} />
      <KeyValueRow label="Revenue Expression" value={trigger.revenueExpression ?? '-'} />
      <KeyValueRow label="Volume Currency" value={trigger.currencyVolumeExpression ?? '-'} />
      <KeyValueRow label="Revenue Currency" value={trigger.currencyRevenueExpression ?? '-'} />
      <KeyValueRow label="Context" value={contextDisplay} />
      <KeyValueRow label="Argument conditions" value={trigger.conditionExpression ?? '-'} />
    </KeyValueTable>
  );
}
